'use client';

import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { APP_MARKETING_FEATURE_FLAGS } from '@/features/app-marketing/constants/app-marketing-constants';
import { useAppMarketingTakeover } from '@/features/app-marketing/contexts/app-marketing-takeover-context';
import { useGlobalContext } from '@/store/contexts/global-context';

const isHomePathname = (pathname: string): boolean => {
    const route = pathname.split('?')[0] || '/';

    if (route === '/') {
        return true;
    }

    let end = route.length;
    while (end > 1 && route.charAt(end - 1) === '/') {
        end -= 1;
    }

    return route.slice(0, end) === '/';
};

/** True on home when the app marketing landing page replaces the normal lobby UI. */
export const useIsAppMarketingTakeoverMode = (): boolean => {
    const pathname = usePathname() ?? '/';
    const { featureFlags } = useGlobalContext();
    const { isTakeoverActive } = useAppMarketingTakeover();
    const isHomeRoute = isHomePathname(pathname);
    const hasFeatureFlag = Boolean(featureFlags?.[APP_MARKETING_FEATURE_FLAGS.SHOW_APP_MARKETING]);

    return isHomeRoute && (hasFeatureFlag || isTakeoverActive);
};

type AppMarketingTakeoverGateProps = {
    children: ReactNode;
};

/** Hides lobby UI such as search, nav, auth buttons, smart banner, and bottom bar during takeover. */
export const AppMarketingTakeoverGate = ({ children }: AppMarketingTakeoverGateProps) => {
    if (useIsAppMarketingTakeoverMode()) {
        return null;
    }

    return <>{children}</>;
};
